import { sortByCommonName } from './plantCatalogue';

/**
 * A plant's category, as the deck it sits in (SPEC-025 D-1).
 *
 * The detail page is a card in a deck of its own category, and a sideways swipe
 * deals the next card. That replaces the endless companion walk (SPEC-007): the
 * walk's ranking deliberately PENALISES a same-category candidate, which is the
 * right rule for a rail called "Perfect companions" and the wrong answer to
 * "show me the perennials" — the question the shelf was just asked.
 *
 * Membership is `plant.category` and the order is `sortByCommonName`, which is
 * the call `buildCategoryRails` already makes. One ordering, not two: the plant a
 * swipe lands on has to be the plant that was next along the shelf the reader
 * came from, and a second sort here would drift from that silently.
 *
 * Pure, like every other module in here, and it carries no state at all. The walk
 * needed a deque in the history entry because a path has a memory; a deck is a
 * ring, and the plant in the URL is a complete description of where in it the
 * reader is (D-1, D-3).
 *
 * A RING, since D-2 as revised: the last card of a category is followed by the
 * first, and the first is preceded by the last. The deck was bounded in the first
 * cut of this spec, and what that bought — a stack that thinned to nothing so a
 * reader could SEE a category ending — cost a dead end at the end of a category
 * of three. Browsing that stops is browsing a gardener has to back out of.
 */

/** Index arithmetic on a ring: `-1` is the last card, `length` is the first. */
function wrap(index, length) {
    return ((index % length) + length) % length;
}

/** What a plant with no category — or no plant at all — is in. */
export const EMPTY_DECK = {
    categoryKey: null,
    cards: [],
    index: -1,
    previous: null,
    next: null,
    total: 0,
};

/**
 * @param {Array} plants - the joined catalogue.
 * @param {string} plantId - the plant on screen.
 * @returns {{categoryKey, cards, index, previous, next, total}} `previous` and
 *   `next` wrap around the ring, so neither is ever null in a category of two or
 *   more. They are null only where there is genuinely nowhere to go: a category
 *   of one, where every step would land back on the card already showing.
 */
export function buildDeck(plants, plantId, { languageCode = 'en' } = {}) {
    const plant = (plants || []).find((candidate) => candidate.id === plantId);

    if (!plant?.category) {
        return EMPTY_DECK;
    }

    const cards = sortByCommonName(
        plants.filter((candidate) => candidate.category === plant.category),
        languageCode,
    );
    const index = cards.findIndex((card) => card.id === plantId);
    const step = (offset) => (cards.length > 1 ? cards[wrap(index + offset, cards.length)] : null);

    return {
        categoryKey: plant.category,
        cards,
        index,
        previous: step(-1),
        next: step(1),
        total: cards.length,
    };
}

/**
 * Where a released swipe goes, or `null` if it goes nowhere — which now happens
 * only in a category of one.
 *
 * The direction words are the gesture's, not the list's: dragging left brings
 * the next card in from the right, which is the direction a page turns forward
 * (`readGesture` in the motion hook says the same thing).
 */
export function deckStep(deck, direction) {
    return (direction === 'back' ? deck.previous : deck.next) ?? null;
}

/**
 * The cards drawn as slivers behind the current one, nearest first (D-4).
 *
 * Capped at three, and the first of them is invisible: it sits at exactly the
 * front card's box, because it is the card about to take that box and the picture
 * it already shows must not move when it does (D-25). The two behind it are what
 * a reader actually sees of the stack.
 *
 * It follows the ring, so on the last card of a category the cards behind are the
 * first ones again. That is the honest picture now that the deck wraps: the stack
 * says "this is a deck and there is more of it", which is true everywhere, rather
 * than counting down to an end that no longer exists.
 *
 * Never a card twice, and never the card in front: a category of two shows one
 * sliver, and a category of one shows none.
 *
 * It follows the DIRECTION the reader is going, because the card behind is the
 * card they are about to see (D-23). At rest that is forward; mid-gesture it is
 * whichever way the finger went, so the picture waiting under the card being
 * dealt away is the picture of the plant actually arriving.
 */
export const MAX_STACK_DEPTH = 3;

export function deckStack(deck, direction = 'forward') {
    const depth = Math.min(MAX_STACK_DEPTH, Math.max(deck.total - 1, 0));
    const towards = direction === 'back' ? -1 : 1;

    return Array.from(
        { length: depth },
        (unused, step) => deck.cards[wrap(deck.index + (step + 1) * towards, deck.total)],
    );
}

/**
 * The deck as SEATS: every card that can be on screen in the next moment, and
 * which role it is playing (SPEC-025 D-29).
 *
 *   FRONT     the card in hand. Carries the gesture and the forward deal.
 *   REVEAL    the next plant, in the front card's exact box, one z below it. A
 *             forward swipe throws the front card off and this is simply there —
 *             it never had to arrive.
 *   INCOMING  the PREVIOUS plant, parked off the edge it left by, ABOVE the front
 *             card. A backward swipe pulls it back on top: the deal undone rather
 *             than a second kind of reveal.
 *   PEEK      the edge that says there is a deck here. Draws no picture.
 *
 * One list, keyed by plant, because that is the whole mechanism: a step MOVES the
 * node that was behind into the front role rather than rewriting the node in
 * front. Rewriting a live `<img>` tears its frame down and re-attaches the bitmap
 * — which is the repaint every decision from D-23 to D-28 was working around.
 *
 * Both directions therefore hold a mounted, decoded card at all times, which is
 * what makes reversing mid-browse instant: there is nothing to add when the
 * direction becomes obvious, because both were already there.
 */
export function deckSeats(deck) {
    if (!deck.total) {
        return [];
    }

    const at = (offset) => deck.cards[wrap(deck.index + offset, deck.total)];
    const seats = [{ plant: at(0), role: 'front', seatDepth: 0 }];
    const seen = new Set([at(0).id]);

    // Order matters only for the ones that overlap: a plant cannot hold two seats,
    // and in a category of two the same card is both what is next and what is
    // previous. Reveal wins, because forward is the direction a deck at rest is
    // pointing.
    [
        { plant: at(1), role: 'reveal', seatDepth: 1 },
        { plant: at(-1), role: 'incoming', seatDepth: -1 },
        { plant: at(2), role: 'peek', seatDepth: 2 },
    ].forEach((seat) => {
        if (!seen.has(seat.plant.id)) {
            seen.add(seat.plant.id);
            seats.push(seat);
        }
    });

    return seats;
}
